import {
  Avatar,
  AvatarGroup,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Grid,
  Image,
  Skeleton,
  SkeletonText,
  Text,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { BackLink } from '../../../components/BackLink'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { DetailField } from '../../../components/DetailField'
import { FilePreviewList, useFilePicker } from '../../../components/FileInput'
import { FormModal } from '../../../components/FormModal'
import { Icon } from '../../../components/Icon'
import { PageTitle } from '../../../components/PageTitle'
import { RichTextField } from '../../../components/RichTextField'
import { SectionCard, SectionTitle } from '../../../components/SectionCard'
import { StatusBadge } from '../../../components/StatusBadge'
import WysiwygContent from '../../../components/WysiwygContent'
import { useUser } from '../../../contexts/UserContext'
import { formatDateTime, formatPhone, useHasRole, useAppToast } from '../../../helpers'
import {
  ESupportTicketAssignee,
  ESupportTicketStatus,
  ISupportTicketComplete
} from '../../../services/Support/interfaces'
import { SupportService } from '../../../services/Support/SupportService'
import { SUPPORT_TICKET_STATUS } from './statuses'
import { SUPPORT_ATTACHMENT_MAX_MB } from './SupportCreateModal'

export default function SupportViewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useAppToast()
  const [supportTicket, setSupportTicket] = useState<ISupportTicketComplete | null>(null)
  const supportService = new SupportService()
  const [commentText, setCommentText] = useState('')
  const [commentFiles, setCommentFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)
  const currentUserId = useUser()?.id
  const [loading, setLoading] = useState(false)
  const [loadingWatchingToggle, setLoadingWatchingToggle] = useState(false)
  const [togglingStatus, setTogglingStatus] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const commentsEndRef = useRef<HTMLDivElement>(null)
  const hasRole = useHasRole()
  const permissions = {
    viewSupportRequests: hasRole('view_support_requests')
  }

  // Resolve modal
  const { isOpen: isResolveOpen, onOpen: onResolveOpen, onClose: onResolveClose } = useDisclosure()
  const [resolutionMessage, setResolutionMessage] = useState('')
  const [resolutionError, setResolutionError] = useState('')

  // Watchers inline
  const [watchers, setWatchers] = useState<{ id: number; name: string; picture: string }[]>([])

  // Delete
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const loadWatchers = useCallback(() => {
    supportService
      .listTicketWatchers(Number(id))
      .then((data) => setWatchers(data.watchers))
      .catch(() => setWatchers([]))
  }, [id])

  const loadSupportTicket = useCallback(() => {
    setLoading(true)
    supportService
      .show(Number(id))
      .then((data) => {
        setSupportTicket(data.supportTicket)
        loadWatchers()
      })
      .catch(() => {
        toast.error('Não foi possível carregar os detalhes do chamado')
        navigate('/support')
      })
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    loadSupportTicket()
  }, [loadSupportTicket])

  // Polling automático a cada 60 segundos
  useEffect(() => {
    const interval = setInterval(loadSupportTicket, 60000)
    return () => clearInterval(interval)
  }, [loadSupportTicket])

  const handleSubmitComment = (switchAssignee: boolean) => {
    if (!commentText.trim() || commentText === '<p><br></p>') return
    setSubmitting(true)
    supportService
      .createComment(Number(id), commentText.trim(), commentFiles, switchAssignee)
      .then(() => {
        setErrors({})
        toast.success('Comentário adicionado')
        setCommentText('')
        setCommentFiles([])
        loadSupportTicket()
        setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 300)
      })
      .catch((error: unknown) => {
        const axiosError = error as AxiosError<{ errors: Record<string, string[]> }>
        if (axiosError.response?.data.errors) {
          setErrors(axiosError.response.data.errors)
          toast.error('Erro ao enviar comentário', 'Verifique os campos destacados e tente novamente.')
        } else {
          toast.error('Não foi possível adicionar o comentário')
        }
      })
      .finally(() => setSubmitting(false))
  }

  // O anexo do comentário não é um campo de formulário com rótulo: é um botão "Anexar arquivos"
  // na mesma linha dos botões de enviar, ao lado do editor. Uma área de soltar ali quebraria a
  // linha, então este é o segundo uso do hook — mesma validação e mesmo token do anexo do
  // chamado, layout próprio.
  const commentFilePicker = useFilePicker({
    accept: 'attachment',
    maxSizeMB: SUPPORT_ATTACHMENT_MAX_MB,
    files: commentFiles,
    multiple: true,
    isDisabled: submitting,
    onPick: (accepted) => setCommentFiles((prev) => [...prev, ...accepted])
  })

  const handleResolveTicket = async () => {
    if (!supportTicket) return
    if (resolutionMessage.trim().length < 10) {
      setResolutionError('A mensagem de resolução deve ter pelo menos 10 caracteres.')
      return
    }
    setTogglingStatus(true)
    try {
      await supportService.update(Number(id), {
        status: ESupportTicketStatus.RESOLVED,
        resolution_message: resolutionMessage.trim()
      })
      toast.success('Chamado encerrado')
      setResolutionMessage('')
      setResolutionError('')
      onResolveClose()
      loadSupportTicket()
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ errors: Record<string, string[]> }>
      if (axiosError.response?.data.errors?.resolution_message) {
        setResolutionError(axiosError.response.data.errors.resolution_message[0])
      } else {
        toast.error('Não foi possível encerrar o chamado')
      }
    } finally {
      setTogglingStatus(false)
    }
  }

  const handleReopenTicket = async () => {
    if (!supportTicket) return
    setTogglingStatus(true)
    try {
      await supportService.update(Number(id), { status: ESupportTicketStatus.OPEN })
      toast.success('Chamado reaberto')
      loadSupportTicket()
    } catch {
      toast.error('Não foi possível reabrir o chamado')
    } finally {
      setTogglingStatus(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await supportService.destroy(Number(id))
      toast.success('Chamado excluído com sucesso')
      navigate('/support')
    } catch {
      toast.error('Não foi possível excluir o chamado')
    } finally {
      setDeleting(false)
    }
  }

  const handleSwitchWatch = async () => {
    if (!supportTicket || loadingWatchingToggle) return

    setLoadingWatchingToggle(true)
    try {
      if (supportTicket.is_watching) {
        await supportService.unwatchTicket(Number(id))
        toast.success('Você não está mais observando o chamado')
      } else {
        await supportService.watchTicket(Number(id))
        toast.success('Você está observando o chamado')
      }
      loadSupportTicket()
    } catch {
      toast.error('Não foi possível alterar o status de observação do chamado')
    } finally {
      setLoadingWatchingToggle(false)
    }
  }

  const isCommentEmpty = !commentText.trim() || commentText === '<p><br></p>'

  return (
    // A calha da página vem do `.dashboard-content` (Dashboard.css) e a raiz não a repete.
    // O `maxW` fica porque esta tela é uma **leitura**: a conversa do chamado em linha cheia num
    // monitor largo passa dos 200 caracteres por linha e fica ilegível.
    <Box maxW="1100px" mx="auto">
      {/* Top bar */}
      <Flex align="center" justify="space-between" mb={6} flexWrap="wrap" gap={3}>
        <BackLink to="/support" />

        {supportTicket && (
          <Flex align="center" gap={2}>
            <Button
              size="sm"
              colorScheme="red"
              variant="ghost"
              leftIcon={<Icon name="lni lni-trash-can" />}
              onClick={() => setShowDeleteConfirm(true)}
            >
              Excluir
            </Button>
            {supportTicket.status === ESupportTicketStatus.OPEN ? (
              <Button
                size="sm"
                colorScheme="green"
                variant="solid"
                leftIcon={<Icon name="lni lni-checkmark-circle" />}
                isLoading={togglingStatus}
                onClick={onResolveOpen}
              >
                Encerrar Chamado
              </Button>
            ) : (
              <Button
                size="sm"
                colorScheme="orange"
                variant="outline"
                leftIcon={<Icon name="lni lni-reload" />}
                isLoading={togglingStatus}
                onClick={handleReopenTicket}
              >
                Reabrir Chamado
              </Button>
            )}
            <StatusBadge
              status={supportTicket.status}
              statuses={SUPPORT_TICKET_STATUS}
              py={1}
              fontWeight="bold"
              textTransform="uppercase"
            />
          </Flex>
        )}
      </Flex>

      <PageTitle title="Chamado de Suporte" description="Detalhes, histórico e respostas do chamado" mb={5} />

      {!supportTicket ? (
        <SupportViewPageSkeleton />
      ) : (
        <>
          {/* Header card */}
          <SectionCard mb={5}>
            <Flex align="center" gap={3} mb={4}>
              <Avatar size="md" name={supportTicket.user.name} src={supportTicket.user.picture} />
              <Box>
                <Text fontSize="lg" fontWeight="bold" lineHeight="short">
                  #{supportTicket.id} - {supportTicket.resume}
                </Text>
                <Text variant="caption">
                  Aberto por <strong>{supportTicket.user.name}</strong> em{' '}
                  <strong>{formatDateTime(supportTicket.created_at)}</strong>
                </Text>
              </Box>
            </Flex>

            <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }} gap={4}>
              <DetailField label="Módulo" value={supportTicket.module} />
              <DetailField label="Telefone" value={formatPhone(supportTicket.phone)} />
              <DetailField label="Responsável atual">
                <Badge
                  colorScheme={supportTicket.current_assignee === ESupportTicketAssignee.SUPPORT ? 'cyan' : 'purple'}
                >
                  {supportTicket.current_assignee === ESupportTicketAssignee.REQUESTER ? 'Solicitante' : 'Suporte'}
                </Badge>
              </DetailField>
            </Grid>

            {/* Watchers inline */}
            {permissions.viewSupportRequests && (
              <>
                <Divider my={4} />
                <Flex align="center" justify="space-between">
                  <Box>
                    <SectionTitle variant="mini">Observadores</SectionTitle>
                    {watchers.length > 0 ? (
                      <AvatarGroup size="xs" max={5} spacing="-0.5rem">
                        {watchers.map((w) => (
                          <Avatar key={w.id} name={w.name} src={w.picture} />
                        ))}
                      </AvatarGroup>
                    ) : (
                      <Text variant="caption">Nenhum observador</Text>
                    )}
                  </Box>
                  {currentUserId != null && supportTicket.user.id !== Number(currentUserId) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme={supportTicket.is_watching ? 'red' : 'blue'}
                      leftIcon={<Icon name="lni lni-eye" />}
                      isLoading={loadingWatchingToggle}
                      onClick={handleSwitchWatch}
                    >
                      {supportTicket.is_watching ? 'Parar de Observar' : 'Observar'}
                    </Button>
                  )}
                </Flex>
              </>
            )}
          </SectionCard>

          {/* Description */}
          <SectionCard title="Descrição do Problema" mb={5}>
            <WysiwygContent content={supportTicket.description} fontSize="sm" />
          </SectionCard>

          {/* Attachments */}
          {supportTicket.attachments.length > 0 && (
            <SectionCard title={`Anexos (${supportTicket.attachments.length})`} mb={5}>
              <Grid templateColumns="repeat(auto-fill, minmax(140px, 1fr))" gap={3}>
                {supportTicket.attachments.map((attachment, i) => {
                  const isPdf = attachment.path.toLowerCase().endsWith('.pdf')
                  return (
                    <Box
                      key={i}
                      as="a"
                      href={attachment.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      borderRadius="md"
                      overflow="hidden"
                      border="1px solid"
                      borderColor="gray.200"
                      cursor="pointer"
                      _hover={{ borderColor: 'blue.300', boxShadow: 'sm' }}
                      transition="all 0.15s"
                      display="block"
                    >
                      {isPdf ? (
                        <Flex h="90px" align="center" justify="center" direction="column" bg="gray.100">
                          <Icon name="lni lni-empty-file" size="xl" color="red.600" />
                          <Text variant="caption" mt={1}>
                            PDF
                          </Text>
                        </Flex>
                      ) : (
                        <Image src={attachment.path} alt={`Anexo ${i + 1}`} w="100%" h="90px" objectFit="cover" />
                      )}
                      <Box px={2} py={1} bg="gray.50">
                        <Text variant="caption" noOfLines={1}>
                          Anexo {i + 1}
                        </Text>
                      </Box>
                    </Box>
                  )
                })}
              </Grid>
            </SectionCard>
          )}

          {/* Acompanhamento */}
          <SectionCard
            title={`Acompanhamento (${supportTicket.comments.length})`}
            actions={
              <Button
                size="xs"
                colorScheme="gray"
                variant="ghost"
                isLoading={loading}
                loadingText="Atualizando"
                onClick={loadSupportTicket}
                leftIcon={<Icon name="lni lni-reload" />}
              >
                Atualizar
              </Button>
            }
          >
            {supportTicket.comments.length === 0 ? (
              <Text variant="caption" mb={4} textAlign="center" py={6}>
                Nenhum comentário ainda. Seja o primeiro a comentar.
              </Text>
            ) : (
              <VStack spacing={4} align="stretch" mb={5}>
                {supportTicket.comments.map((comment) => {
                  const isCurrentUser = currentUserId && comment.user.id === parseInt(currentUserId)
                  const isRequester = comment.user.id === supportTicket.user.id
                  return (
                    <Flex
                      key={comment.id}
                      justify={isCurrentUser ? 'flex-end' : 'flex-start'}
                      align="flex-start"
                      gap={3}
                    >
                      {!isCurrentUser && (
                        <Avatar size="sm" name={comment.user.name} src={comment.user.picture} mt={1} />
                      )}
                      <Box
                        bg={isCurrentUser ? 'pink.50' : 'gray.50'}
                        borderRadius="xl"
                        px={4}
                        py={3}
                        border="1px solid"
                        borderColor={isCurrentUser ? 'pink.200' : 'gray.200'}
                        maxW="75%"
                      >
                        <Flex align="center" gap={2} mb={2} flexWrap="wrap">
                          <Text fontSize="sm" fontWeight="bold" color="gray.700">
                            {comment.user.name}
                          </Text>
                          <Badge colorScheme={isRequester ? 'purple' : 'cyan'} variant="subtle">
                            {isRequester ? 'Solicitante' : 'Suporte'}
                          </Badge>
                          {isCurrentUser && (
                            <Badge colorScheme="pink" variant="subtle">
                              Você
                            </Badge>
                          )}
                          <Text variant="caption" ml="auto">
                            {formatDateTime(comment.created_at)}
                          </Text>
                        </Flex>
                        <Box fontSize="sm" color="gray.600">
                          <WysiwygContent content={comment.message} fontSize="sm" />
                        </Box>
                        {comment.attachments && comment.attachments.length > 0 && (
                          <Flex mt={3} flexWrap="wrap" gap={2}>
                            {comment.attachments.map((att, ai) => {
                              const isPdf = att.path.toLowerCase().endsWith('.pdf')
                              return (
                                <Box
                                  key={ai}
                                  as="a"
                                  href={att.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  borderRadius="md"
                                  overflow="hidden"
                                  border="1px solid"
                                  borderColor="gray.200"
                                  w="80px"
                                  display="block"
                                  _hover={{ borderColor: 'blue.300' }}
                                  transition="all 0.15s"
                                >
                                  {isPdf ? (
                                    <Flex h="60px" align="center" justify="center" direction="column" bg="gray.100">
                                      <Icon name="lni lni-empty-file" size="xl" color="red.600" />
                                      <Text variant="caption" mt={0.5}>
                                        PDF
                                      </Text>
                                    </Flex>
                                  ) : (
                                    <Image src={att.path} alt={`Anexo ${ai + 1}`} w="100%" h="60px" objectFit="cover" />
                                  )}
                                </Box>
                              )
                            })}
                          </Flex>
                        )}
                      </Box>
                      {isCurrentUser && <Avatar size="sm" name={comment.user.name} src={comment.user.picture} mt={1} />}
                    </Flex>
                  )
                })}
                <div ref={commentsEndRef} />
              </VStack>
            )}

            <Divider mb={5} />

            {/* Add comment */}
            <Box>
              <SectionTitle variant="mini" mb={3}>
                Adicionar Comentário
              </SectionTitle>
              <RichTextField
                error={errors.message}
                mb={3}
                toolbar="comment"
                minH="100px"
                value={commentText}
                onChange={setCommentText}
                placeholder="Escreva um comentário..."
                onKeyDown={(e) => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !isCommentEmpty && !submitting) {
                    e.preventDefault()
                    const canSwitchAssignee =
                      !!supportTicket &&
                      !!currentUserId &&
                      ((supportTicket.current_assignee === ESupportTicketAssignee.REQUESTER &&
                        supportTicket.user.id === parseInt(currentUserId)) ||
                        (supportTicket.current_assignee === ESupportTicketAssignee.SUPPORT &&
                          supportTicket.user.id !== parseInt(currentUserId)))
                    handleSubmitComment(canSwitchAssignee)
                  }
                }}
              />
              <Flex justify="space-between" align="flex-start" gap={3} flexWrap="wrap">
                <FormControl
                  width={'auto'}
                  isInvalid={!!Object.entries(errors).find(([key]) => key.startsWith('attachments.'))}
                >
                  <Box>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={commentFilePicker.open}
                      isLoading={commentFilePicker.isProcessing}
                      leftIcon={<Icon name="lni lni-paperclip" />}
                    >
                      Anexar arquivos
                    </Button>
                    <input {...commentFilePicker.inputProps} />
                    <FilePreviewList
                      files={commentFiles}
                      variant="thumbnail"
                      isDisabled={submitting}
                      onRemove={(index) => setCommentFiles((prev) => prev.filter((_, i) => i !== index))}
                    />
                    <FormErrorMessage>
                      {Object.entries(errors).find(([key]) => key.startsWith('attachments.'))?.[1]?.[0]}
                    </FormErrorMessage>
                  </Box>
                </FormControl>
                <Flex gap={2}>
                  <Button
                    size="sm"
                    colorScheme="magenta"
                    variant={'outline'}
                    onClick={() => handleSubmitComment(false)}
                    isDisabled={isCommentEmpty}
                    isLoading={submitting}
                  >
                    Adicionar comentário
                  </Button>
                  {supportTicket &&
                    currentUserId &&
                    ((supportTicket.current_assignee === ESupportTicketAssignee.REQUESTER &&
                      supportTicket.user.id === parseInt(currentUserId)) ||
                    (supportTicket.current_assignee === ESupportTicketAssignee.SUPPORT &&
                      supportTicket.user.id !== parseInt(currentUserId)) ? (
                      <Button
                        size="sm"
                        colorScheme="magenta"
                        onClick={() => handleSubmitComment(true)}
                        isDisabled={isCommentEmpty}
                        isLoading={submitting}
                      >
                        Enviar para{' '}
                        {supportTicket.current_assignee === ESupportTicketAssignee.REQUESTER
                          ? 'Suporte'
                          : 'Solicitante'}
                      </Button>
                    ) : null)}
                </Flex>
              </Flex>
            </Box>
          </SectionCard>
        </>
      )}

      {/* Modal de resolução */}
      <FormModal
        isOpen={isResolveOpen}
        onClose={onResolveClose}
        title="Encerrar Chamado"
        onSubmit={handleResolveTicket}
        confirmLabel="Encerrar Chamado"
        isLoading={togglingStatus}
      >
        <Text variant="caption" mb={3}>
          Descreva a resolução do chamado <strong>#{supportTicket?.id}</strong>. Esta mensagem será registrada como um
          comentário no chamado.
        </Text>
        <RichTextField
          error={resolutionError}
          toolbar="comment"
          minH="120px"
          value={resolutionMessage}
          onChange={setResolutionMessage}
          placeholder="Descreva como o problema foi resolvido..."
        />
      </FormModal>

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        action="delete"
        entity="chamado"
        name={`#${supportTicket?.id}`}
        isLoading={deleting}
      />
    </Box>
  )
}

function SupportViewPageSkeleton() {
  return (
    <>
      <SectionCard mb={5}>
        <Flex align="center" gap={3} mb={4}>
          <Skeleton height="48px" width="48px" borderRadius="full" />
          <Box flex={1}>
            <Skeleton height="20px" width="350px" mb={2} />
            <Skeleton height="14px" width="250px" />
          </Box>
        </Flex>
        <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }} gap={4}>
          <Box>
            <Skeleton height="12px" width="60px" mb={2} />
            <Skeleton height="16px" width="120px" />
          </Box>
          <Box>
            <Skeleton height="12px" width="60px" mb={2} />
            <Skeleton height="16px" width="120px" />
          </Box>
          <Box>
            <Skeleton height="12px" width="80px" mb={2} />
            <Skeleton height="20px" width="80px" />
          </Box>
        </Grid>
      </SectionCard>

      <SectionCard mb={5}>
        <Skeleton height="12px" width="160px" mb={3} />
        <SkeletonText noOfLines={4} spacing="2" skeletonHeight="3" />
      </SectionCard>

      <SectionCard>
        <Skeleton height="12px" width="140px" mb={4} />
        <VStack spacing={4} align="stretch" mb={5}>
          <Flex align="flex-start" gap={3}>
            <Skeleton height="32px" width="32px" borderRadius="full" />
            <Box bg="gray.50" borderRadius="xl" px={4} py={3} border="1px solid" borderColor="gray.200" maxW="60%">
              <Flex gap={2} mb={2}>
                <Skeleton height="14px" width="100px" />
                <Skeleton height="14px" width="80px" ml="auto" />
              </Flex>
              <SkeletonText noOfLines={2} spacing="2" skeletonHeight="3" />
            </Box>
          </Flex>
          <Flex align="flex-start" gap={3} justify="flex-end">
            <Box bg="pink.50" borderRadius="xl" px={4} py={3} border="1px solid" borderColor="pink.200" maxW="60%">
              <Flex gap={2} mb={2}>
                <Skeleton height="14px" width="100px" />
                <Skeleton height="14px" width="80px" ml="auto" />
              </Flex>
              <SkeletonText noOfLines={2} spacing="2" skeletonHeight="3" />
            </Box>
            <Skeleton height="32px" width="32px" borderRadius="full" />
          </Flex>
        </VStack>

        <Divider mb={4} />
        <Skeleton height="12px" width="160px" mb={3} />
        <Skeleton height="96px" borderRadius="md" mb={3} />
        <Flex justify="flex-end">
          <Skeleton height="32px" width="140px" borderRadius="md" />
        </Flex>
      </SectionCard>
    </>
  )
}
